import React, { useState, useRef } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber, Space, Tag, Typography, Row, Col, Divider, message, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PrinterOutlined, DollarOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Invoices = () => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [printLanguage, setPrintLanguage] = useState(i18n.language);
    const [items, setItems] = useState([{ description: '', quantity: 1, unitPrice: 0, total: 0 }]);
    const printRef = useRef();

    // Helper function to format currency
    const formatCurrency = (amount, currency = 'USD') => {
        const symbol = currency === 'IQD' ? 'IQD' : '$';
        return `${symbol}${amount?.toLocaleString() || '0'}`;
    };

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Invoice-${selectedInvoice?.invoiceNumber || 'Draft'}`,
    });

    // Fetch data
    const { data: invoices, isLoading } = useQuery('invoices', async () => {
        const response = await api.get('/invoices');
        return response.data.data;
    });

    const { data: students, isLoading: studentsLoading, error: studentsError } = useQuery('students', async () => {
        const response = await api.get('/students');
        return response.data.data;
    }, {
        onError: (error) => {
            console.error('Error fetching students:', error);
        }
    });

    const { data: staff, isLoading: staffLoading, error: staffError } = useQuery('staff', async () => {
        const response = await api.get('/staff');
        return response.data.data;
    }, {
        onError: (error) => {
            console.error('Error fetching staff:', error);
        }
    });

    const { data: courses, isLoading: coursesLoading, error: coursesError } = useQuery('courses', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    }, {
        onError: (error) => {
            console.error('Error fetching courses:', error);
        }
    });

    // Create mutation
    const createMutation = useMutation(
        (data) => api.post('/invoices', data),
        {
            onSuccess: () => {
                message.success(t('invoiceCreatedSuccessfully'));
                queryClient.invalidateQueries('invoices');
                setModalVisible(false);
                form.resetFields();
                setItems([{ description: '', quantity: 1, unitPrice: 0, total: 0 }]);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToCreateInvoice'));
            }
        }
    );

    // Update mutation
    const updateMutation = useMutation(
        (data) => api.put(`/invoices/${data._id}`, data),
        {
            onSuccess: () => {
                message.success(t('invoiceUpdatedSuccessfully'));
                queryClient.invalidateQueries('invoices');
                setModalVisible(false);
                setEditingInvoice(null);
                form.resetFields();
                setItems([{ description: '', quantity: 1, unitPrice: 0, total: 0 }]);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUpdateInvoice'));
            }
        }
    );

    // Delete mutation
    const deleteMutation = useMutation(
        (id) => api.delete(`/invoices/${id}`),
        {
            onSuccess: () => {
                message.success(t('invoiceDeletedSuccessfully'));
                queryClient.invalidateQueries('invoices');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToDeleteInvoice'));
            }
        }
    );

    // Payment mutation
    const paymentMutation = useMutation(
        ({ id, data }) => api.post(`/invoices/${id}/payment`, data),
        {
            onSuccess: () => {
                message.success(t('paymentRecordedSuccessfully'));
                queryClient.invalidateQueries('invoices');
                setPaymentModalVisible(false);
                setSelectedInvoice(null);
                paymentForm.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToRecordPayment'));
            }
        }
    );

    const handleAddItem = () => {
        setItems([...items, { description: '', quantity: 1, unitPrice: 0, total: 0 }]);
    };

    const handleRemoveItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems.length > 0 ? newItems : [{ description: '', quantity: 1, unitPrice: 0, total: 0 }]);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;

        if (field === 'quantity' || field === 'unitPrice') {
            newItems[index].total = newItems[index].quantity * newItems[index].unitPrice;
        }

        setItems(newItems);

        // Update subtotal in form
        const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
        form.setFieldsValue({ subtotal });
    };

    const handleSubmit = async (values) => {
        const invoiceData = {
            ...values,
            items,
            invoiceDate: values.invoiceDate?.toDate(),
            dueDate: values.dueDate?.toDate(),
        };

        if (editingInvoice) {
            updateMutation.mutate({ ...invoiceData, _id: editingInvoice._id });
        } else {
            createMutation.mutate(invoiceData);
        }
    };

    const handleEdit = (record) => {
        setEditingInvoice(record);
        setItems(record.items || [{ description: '', quantity: 1, unitPrice: 0, total: 0 }]);
        form.setFieldsValue({
            ...record,
            invoiceDate: record.invoiceDate ? dayjs(record.invoiceDate) : null,
            dueDate: record.dueDate ? dayjs(record.dueDate) : null,
            student: record.student?._id,
            staff: record.staff?._id,
            course: record.course?._id,
        });
        setModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('actionCannotBeUndone'),
            okText: t('delete'),
            okType: 'danger',
            cancelText: t('cancel'),
            onOk: () => deleteMutation.mutate(id),
        });
    };

    const handleRecordPayment = (record) => {
        setSelectedInvoice(record);
        paymentForm.setFieldsValue({
            amount: record.remainingAmount,
        });
        setPaymentModalVisible(true);
    };

    const handlePaymentSubmit = (values) => {
        paymentMutation.mutate({
            id: selectedInvoice._id,
            data: values,
        });
    };

    const handlePreview = (record) => {
        setSelectedInvoice(record);
        setPreviewModalVisible(true);
    };

    const columns = [
        {
            title: t('invoiceNumber'),
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            width: 150,
        },
        {
            title: t('date'),
            dataIndex: 'invoiceDate',
            key: 'invoiceDate',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
            sorter: (a, b) => dayjs(a.invoiceDate).unix() - dayjs(b.invoiceDate).unix(),
        },
        {
            title: t('type'),
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={type === 'payment' ? 'orange' : 'green'}>
                    {t(type)}
                </Tag>
            ),
        },
        {
            title: t('client'),
            key: 'client',
            render: (_, record) => {
                if (record.student) {
                    return `${record.student.firstName} ${record.student.lastName}`;
                }
                if (record.staff) {
                    return `${record.staff.firstName} ${record.staff.lastName}`;
                }
                return record.clientName || '-';
            },
        },
        {
            title: t('totalAmount'),
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount, record) => <Text strong>{formatCurrency(amount, record.currency)}</Text>,
        },
        {
            title: t('paidAmount'),
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            render: (amount, record) => <Text style={{ color: '#52c41a' }}>{formatCurrency(amount, record.currency)}</Text>,
        },
        {
            title: t('remainingAmount'),
            dataIndex: 'remainingAmount',
            key: 'remainingAmount',
            render: (amount, record) => <Text style={{ color: amount > 0 ? '#ff4d4f' : '#52c41a' }}>{formatCurrency(amount, record.currency)}</Text>,
        },
        {
            title: t('paymentStatus'),
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status) => {
                const colors = { paid: 'green', partial: 'orange', pending: 'red', cancelled: 'default' };
                return <Tag color={colors[status]}>{t(status)}</Tag>;
            },
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = { draft: 'default', sent: 'blue', paid: 'green', cancelled: 'red' };
                return <Tag color={colors[status]}>{t(status)}</Tag>;
            },
        },
        {
            title: t('actions'),
            key: 'actions',
            fixed: 'right',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handlePreview(record)}
                    />
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    {record.paymentStatus !== 'paid' && (
                        <Button
                            type="link"
                            icon={<DollarOutlined />}
                            onClick={() => handleRecordPayment(record)}
                        />
                    )}
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record._id)}
                    />
                </Space>
            ),
        },
    ];

    const InvoicePrintTemplate = ({ invoice, language }) => {
        if (!invoice) return null;

        const isRTL = language === 'ar' || language === 'ku';
        const translations = {
            en: {
                invoice: 'INVOICE',
                languageInstitute: 'Language Institute Management System',
                invoiceNumber: 'Invoice Number',
                date: 'Date',
                dueDate: 'Due Date',
                billTo: 'Bill To',
                description: 'Description',
                quantity: 'Qty',
                unitPrice: 'Unit Price',
                total: 'Total',
                subtotal: 'Subtotal',
                tax: 'Tax',
                discount: 'Discount',
                totalAmount: 'Total Amount',
                paidAmount: 'Paid Amount',
                remainingAmount: 'Balance Due',
                notes: 'Notes',
                generatedOn: 'Generated on',
                authorizedSignature: 'Authorized Signature',
                thankYou: 'Thank you for your business!',
                status: 'Status'
            },
            ar: {
                invoice: 'فاتورة',
                languageInstitute: 'نظام إدارة معهد اللغات',
                invoiceNumber: 'رقم الفاتورة',
                date: 'التاريخ',
                dueDate: 'تاريخ الاستحقاق',
                billTo: 'إلى',
                description: 'الوصف',
                quantity: 'الكمية',
                unitPrice: 'السعر',
                total: 'المجموع',
                subtotal: 'المجموع الفرعي',
                tax: 'الضريبة',
                discount: 'الخصم',
                totalAmount: 'المبلغ الإجمالي',
                paidAmount: 'المبلغ المدفوع',
                remainingAmount: 'المبلغ المتبقي',
                notes: 'ملاحظات',
                generatedOn: 'تم الإنشاء في',
                authorizedSignature: 'التوقيع المعتمد',
                thankYou: 'شكراً لتعاملكم معنا!',
                status: 'الحالة'
            },
            ku: {
                invoice: 'وەسڵ',
                languageInstitute: 'سیستەمی بەڕێوەبردنی پەیمانگای زمان',
                invoiceNumber: 'ژمارەی وەسڵ',
                date: 'بەروار',
                dueDate: 'بەرواری کۆتایی',
                billTo: 'بۆ',
                description: 'وەسف',
                quantity: 'دانە',
                unitPrice: 'نرخ',
                total: 'کۆی گشتی',
                subtotal: 'کۆی لاوەکی',
                tax: 'باج',
                discount: 'داشکاندن',
                totalAmount: 'کۆی گشتی',
                paidAmount: 'بڕی دراو',
                remainingAmount: 'بڕی ماوە',
                notes: 'تێبینی',
                generatedOn: 'دروستکراوە لە',
                authorizedSignature: 'واژووی مۆڵەت پێدراو',
                thankYou: 'سوپاس بۆ کارلێک کردنتان!',
                status: 'دۆخ'
            }
        };

        const tr = (key) => translations[language]?.[key] || translations.en[key] || key;

        const getStatusStyle = (status) => {
            const styles = {
                paid: 'paid-badge',
                sent: 'sent-badge',
                draft: 'draft-badge',
                cancelled: 'cancelled-badge'
            };
            return styles[status] || 'draft-badge';
        };

        return (
            <div>
                <style>
                    {`
                        @font-face {
                            font-family: 'Inter';
                            font-style: normal;
                            font-weight: 400 800;
                            src: url('https://fonts.gstatic.com/s/inter/v12/UcCO3H6m3Fh5sU5Z4hY.woff2') format('woff2');
                        }
                        @media print {
                            @page { size: A4; margin: 0; }
                            body { margin: 0; padding: 0; }
                            .invoice-print-container {
                                box-shadow: none !important;
                                border: 2px solid #764ba2 !important;
                                background: #fff !important;
                            }
                            .invoice-header {
                                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
                                color: #fff !important;
                            }
                            .logo-watermark {
                                opacity: 0.08 !important;
                                position: absolute !important;
                                top: 40%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                font-size: 180px;
                                font-weight: 900;
                                color: #764ba2;
                                pointer-events: none;
                                z-index: 0;
                            }
                        }
                        .invoice-print-container {
                            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                            direction: ${isRTL ? 'rtl' : 'ltr'};
                            max-width: 210mm;
                            min-height: 297mm;
                            margin: 0 auto;
                            background: #ffffff;
                            color: #1a1a1a;
                            position: relative;
                            box-shadow: 0 0 30px rgba(0,0,0,0.08);
                            border: 2px solid #764ba2;
                        }

                        .invoice-header {
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                            padding: 40px;
                            position: relative;
                            overflow: hidden;
                        }

                        .invoice-header::before {
                            content: '';
                            position: absolute;
                            top: -40%;
                            ${isRTL ? 'left' : 'right'}: -5%;
                            width: 250px;
                            height: 250px;
                            background: rgba(255,255,255,0.08);
                            border-radius: 50%;
                        }

                        .header-flex {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            position: relative;
                            z-index: 2;
                        }

                        .company-info {
                            display: flex;
                            gap: 20px;
                            align-items: center;
                        }

                        .logo-box {
                            background: rgba(255,255,255,0.15);
                            backdrop-filter: blur(10px);
                            width: 70px;
                            height: 70px;
                            border-radius: 14px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border: 2px solid rgba(255,255,255,0.25);
                            font-size: 36px;
                            font-weight: 800;
                            color: white;
                        }

                        .company-details h1 {
                            color: white;
                            font-size: 26px;
                            font-weight: 700;
                            letter-spacing: -0.5px;
                            margin: 0 0 4px 0;
                        }

                        .company-details p {
                            color: rgba(255,255,255,0.9);
                            font-size: 13px;
                            margin: 0;
                        }

                        .invoice-badge {
                            background: rgba(255,255,255,0.2);
                            backdrop-filter: blur(10px);
                            color: white;
                            padding: 10px 24px;
                            border-radius: 25px;
                            font-size: 12px;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 2px;
                            border: 1px solid rgba(255,255,255,0.3);
                        }

                        .invoice-content {
                            padding: 40px;
                        }

                        .info-grid {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 25px;
                            margin-bottom: 35px;
                        }

                        .info-card {
                            background: #f8f9fc;
                            border: 1px solid #e8eaf0;
                            border-radius: 10px;
                            padding: 22px;
                            position: relative;
                        }

                        .info-card::before {
                            content: '';
                            position: absolute;
                            top: 0;
                            ${isRTL ? 'right' : 'left'}: 0;
                            width: 4px;
                            height: 100%;
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                            border-radius: ${isRTL ? '0 10px 10px 0' : '10px 0 0 10px'};
                        }

                        .info-label {
                            font-size: 11px;
                            font-weight: 700;
                            color: #8b8d98;
                            text-transform: uppercase;
                            letter-spacing: 1.2px;
                            margin-bottom: 14px;
                        }

                        .info-item {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 8px 0;
                            border-bottom: 1px solid #e8eaf0;
                        }

                        .info-item:last-child {
                            border-bottom: none;
                        }

                        .info-key {
                            font-size: 13px;
                            font-weight: 500;
                            color: #5a5c69;
                        }

                        .info-value {
                            font-size: 13px;
                            font-weight: 600;
                            color: #1a1a1a;
                        }

                        .client-name {
                            font-size: 17px;
                            font-weight: 700;
                            color: #1a1a1a;
                            margin-bottom: 10px;
                        }

                        .client-info {
                            font-size: 13px;
                            color: #5a5c69;
                            margin: 5px 0;
                            display: flex;
                            align-items: center;
                            gap: 8px;
                        }

                        .paid-badge {
                            background: #d1f4e0;
                            color: #0d6832;
                            padding: 6px 13px;
                            border-radius: 18px;
                            font-size: 11px;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            display: inline-block;
                        }

                        .sent-badge {
                            background: #cfe2ff;
                            color: #052c65;
                            padding: 6px 13px;
                            border-radius: 18px;
                            font-size: 11px;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            display: inline-block;
                        }

                        .draft-badge {
                            background: #fff3cd;
                            color: #664d03;
                            padding: 6px 13px;
                            border-radius: 18px;
                            font-size: 11px;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            display: inline-block;
                        }

                        .cancelled-badge {
                            background: #f8d7da;
                            color: #58151c;
                            padding: 6px 13px;
                            border-radius: 18px;
                            font-size: 11px;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            display: inline-block;
                        }

                        .invoice-table {
                            width: 100%;
                            border-collapse: separate;
                            border-spacing: 0;
                            margin: 30px 0;
                            border-radius: 10px;
                            overflow: hidden;
                            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                        }

                        .invoice-table thead {
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        }

                        .invoice-table th {
                            padding: 15px 18px;
                            font-size: 12px;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 1px;
                            color: white;
                            text-align: ${isRTL ? 'right' : 'left'};
                        }

                        .invoice-table th.center {
                            text-align: center;
                        }

                        .invoice-table th.right {
                            text-align: right;
                        }

                        .invoice-table tbody tr {
                            background: white;
                        }

                        .invoice-table tbody tr:nth-child(even) {
                            background: #f8f9fc;
                        }

                        .invoice-table td {
                            padding: 14px 18px;
                            font-size: 13px;
                            color: #1a1a1a;
                            border-bottom: 1px solid #e8eaf0;
                        }

                        .invoice-table tbody tr:last-child td {
                            border-bottom: none;
                        }

                        .totals-wrapper {
                            display: flex;
                            justify-content: ${isRTL ? 'flex-start' : 'flex-end'};
                            margin: 35px 0;
                        }

                        .totals-card {
                            width: 360px;
                            background: #f8f9fc;
                            border: 1px solid #e8eaf0;
                            border-radius: 10px;
                            overflow: hidden;
                        }

                        .totals-row {
                            display: flex;
                            justify-content: space-between;
                            padding: 13px 22px;
                            font-size: 13px;
                            border-bottom: 1px solid #e8eaf0;
                        }

                        .totals-label {
                            font-weight: 500;
                            color: #5a5c69;
                        }

                        .totals-value {
                            font-weight: 700;
                            color: #1a1a1a;
                        }

                        .discount-value {
                            color: #dc3545;
                        }

                        .total-amount-row {
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                            padding: 18px 22px;
                            color: white;
                            font-size: 17px;
                            font-weight: 700;
                            border: none;
                            display: flex;
                            justify-content: space-between;
                        }

                        .paid-amount {
                            color: #0d6832;
                            font-weight: 600;
                            background: #f0f9f4;
                            padding: 13px 22px;
                            display: flex;
                            justify-content: space-between;
                            font-size: 13px;
                        }

                        .remaining-amount {
                            color: #dc3545;
                            font-weight: 700;
                            font-size: 15px;
                            background: #fff8f8;
                            padding: 13px 22px;
                            border-top: 2px solid #dc3545;
                            display: flex;
                            justify-content: space-between;
                        }

                        .notes-container {
                            background: #fffbeb;
                            border-${isRTL ? 'right' : 'left'}: 4px solid #fbbf24;
                            border-radius: 8px;
                            padding: 18px;
                            margin: 30px 0;
                        }

                        .notes-title {
                            font-size: 12px;
                            font-weight: 700;
                            color: #92400e;
                            text-transform: uppercase;
                            letter-spacing: 1px;
                            margin-bottom: 10px;
                        }

                        .notes-text {
                            font-size: 13px;
                            color: #78350f;
                            line-height: 1.6;
                        }

                        .invoice-footer {
                            margin-top: 50px;
                            padding-top: 28px;
                            border-top: 2px solid #e8eaf0;
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-end;
                        }

                        .footer-info {
                            font-size: 11px;
                            color: #8b8d98;
                        }

                        .signature-box {
                            text-align: center;
                            min-width: 190px;
                        }

                        .signature-label {
                            font-size: 11px;
                            color: #8b8d98;
                            font-weight: 500;
                            margin-bottom: 45px;
                            text-transform: uppercase;
                            letter-spacing: 1px;
                        }

                        .signature-line {
                            border-top: 2px solid #1a1a1a;
                            padding-top: 6px;
                            font-size: 10px;
                            color: #8b8d98;
                        }

                        .thank-you-section {
                            text-align: center;
                            margin-top: 45px;
                            padding: 22px;
                            background: linear-gradient(135deg, rgba(102, 126, 234, 0.06) 0%, rgba(118, 75, 162, 0.06) 100%);
                            border-radius: 10px;
                        }

                        .thank-you-text {
                            font-size: 15px;
                            font-weight: 600;
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                            -webkit-background-clip: text;
                            -webkit-text-fill-color: transparent;
                            background-clip: text;
                        }

                        .text-center {
                            text-align: center;
                        }

                        .text-right {
                            text-align: right;
                        }

                        .font-bold {
                            font-weight: 700;
                        }

                        .clearfix::after {
                            content: "";
                            display: table;
                            clear: both;
                        }
                    `}
                </style>

                <div className="invoice-print-container">
                    <div className="logo-watermark">P</div>
                    {/* Header */}
                    <div className="invoice-header">
                        <div className="header-flex">
                            <div className="company-info">
                                <div className="logo-box">P</div>
                                <div className="company-details">
                                    <h1>PAYMANGA</h1>
                                    <p>{tr('languageInstitute')}</p>
                                </div>
                            </div>
                            <div className="invoice-badge">{tr('invoice')}</div>
                        </div>
                    </div>

                    <div className="invoice-content">
                        {/* Info Grid */}
                        <div className="info-grid">
                            <div className="info-card">
                                <div className="info-label">{tr('invoice')} {tr('date')}</div>
                                <div className="info-item">
                                    <span className="info-key">{tr('invoiceNumber')}</span>
                                    <span className="info-value">{invoice.invoiceNumber}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-key">{tr('date')}</span>
                                    <span className="info-value">{dayjs(invoice.invoiceDate).format('MMM DD, YYYY')}</span>
                                </div>
                                {invoice.dueDate && (
                                    <div className="info-item">
                                        <span className="info-key">{tr('dueDate')}</span>
                                        <span className="info-value">{dayjs(invoice.dueDate).format('MMM DD, YYYY')}</span>
                                    </div>
                                )}
                                <div style={{ marginTop: '14px' }}>
                                    <span className={getStatusStyle(invoice.status)}>
                                        {invoice.status}
                                    </span>
                                </div>
                            </div>

                            <div className="info-card">
                                <div className="info-label">{tr('billTo')}</div>
                                <div className="client-name">
                                    {invoice.student ? `${invoice.student.firstName} ${invoice.student.lastName}` :
                                        invoice.staff ? `${invoice.staff.firstName} ${invoice.staff.lastName}` :
                                            invoice.clientName}
                                </div>
                                {(invoice.student?.email || invoice.staff?.email || invoice.clientEmail) && (
                                    <div className="client-info">
                                        <span>✉</span>
                                        <span>{invoice.student?.email || invoice.staff?.email || invoice.clientEmail}</span>
                                    </div>
                                )}
                                {(invoice.student?.phone || invoice.staff?.phone || invoice.clientPhone) && (
                                    <div className="client-info">
                                        <span>☎</span>
                                        <span>{invoice.student?.phone || invoice.staff?.phone || invoice.clientPhone}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Table */}
                        <table className="invoice-table">
                            <thead>
                                <tr>
                                    <th>{tr('description')}</th>
                                    <th className="center" style={{ width: '80px' }}>{tr('quantity')}</th>
                                    <th className="right" style={{ width: '130px' }}>{tr('unitPrice')}</th>
                                    <th className="right" style={{ width: '130px' }}>{tr('total')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.items?.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.description}</td>
                                        <td className="text-center">{item.quantity}</td>
                                        <td className="text-right">{formatCurrency(item.unitPrice, invoice.currency)}</td>
                                        <td className="text-right font-bold">{formatCurrency(item.total, invoice.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Totals */}
                        <div className="totals-wrapper">
                            <div className="totals-card">
                                <div className="totals-row">
                                    <span className="totals-label">{tr('subtotal')}</span>
                                    <span className="totals-value">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                                </div>
                                {invoice.tax > 0 && (
                                    <div className="totals-row">
                                        <span className="totals-label">{tr('tax')}</span>
                                        <span className="totals-value">{formatCurrency(invoice.tax, invoice.currency)}</span>
                                    </div>
                                )}
                                {invoice.discount > 0 && (
                                    <div className="totals-row">
                                        <span className="totals-label">{tr('discount')}</span>
                                        <span className="totals-value discount-value">-{formatCurrency(invoice.discount, invoice.currency)}</span>
                                    </div>
                                )}
                                <div className="total-amount-row">
                                    <span>{tr('totalAmount')}</span>
                                    <span>{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
                                </div>
                                {invoice.paidAmount > 0 && (
                                    <div className="paid-amount">
                                        <span>{tr('paidAmount')}</span>
                                        <span>{formatCurrency(invoice.paidAmount, invoice.currency)}</span>
                                    </div>
                                )}
                                {invoice.remainingAmount > 0 && (
                                    <div className="remaining-amount">
                                        <span>{tr('remainingAmount')}</span>
                                        <span>{formatCurrency(invoice.remainingAmount, invoice.currency)}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Notes */}
                        {invoice.notes && (
                            <div className="notes-container">
                                <div className="notes-title">{tr('notes')}</div>
                                <div className="notes-text">{invoice.notes}</div>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="invoice-footer">
                            <div className="footer-info">
                                {tr('generatedOn')}: {dayjs().format('MMM DD, YYYY [at] HH:mm')}
                            </div>
                            <div className="signature-box">
                                <div className="signature-label">{tr('authorizedSignature')}</div>
                                <div className="signature-line">___________________</div>
                            </div>
                        </div>

                        {/* Thank You */}
                        <div className="thank-you-section">
                            <div className="thank-you-text">{tr('thankYou')}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="invoices-page">
            <div className="page-header">
                <Title level={2}>{t('invoices')}</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingInvoice(null);
                        form.resetFields();
                        setItems([{ description: '', quantity: 1, unitPrice: 0, total: 0 }]);
                        setModalVisible(true);
                    }}
                >
                    {t('createInvoice')}
                </Button>
            </div>

            <Card>
                <Table
                    columns={columns}
                    dataSource={invoices}
                    rowKey="_id"
                    loading={isLoading}
                    scroll={{ x: 1400 }}
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${t('total')} ${total} ${t('invoices')}` }}
                />
            </Card>

            {/* Create/Edit Modal */}
            <Modal
                title={editingInvoice ? t('editInvoice') : t('createInvoice')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingInvoice(null);
                    form.resetFields();
                }}
                footer={null}
                width={900}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="type"
                                label={t('type')}
                                rules={[{ required: true, message: t('pleaseSelectType') }]}
                            >
                                <Select placeholder={t('selectType')}>
                                    <Option value="payment">{t('payment')}</Option>
                                    <Option value="receipt">{t('receipt')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="status"
                                label={t('status')}
                                rules={[{ required: true, message: t('pleaseSelectStatus') }]}
                            >
                                <Select placeholder={t('selectStatus')}>
                                    <Option value="draft">{t('draft')}</Option>
                                    <Option value="sent">{t('sent')}</Option>
                                    <Option value="paid">{t('paid')}</Option>
                                    <Option value="cancelled">{t('cancelled')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="invoiceDate" label={t('invoiceDate')} rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="dueDate" label={t('dueDate')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="student" label={t('student')}>
                                <Select
                                    placeholder={t('selectStudent')}
                                    allowClear
                                    showSearch
                                    loading={studentsLoading}
                                    notFoundContent={studentsLoading ? 'Loading...' : studentsError ? 'Error loading students' : 'No students found'}
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {students?.map(student => (
                                        <Option key={student._id} value={student._id}>
                                            {student.firstName} {student.lastName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="staff" label={t('staff')}>
                                <Select
                                    placeholder={t('selectStaff')}
                                    allowClear
                                    showSearch
                                    loading={staffLoading}
                                    notFoundContent={staffLoading ? 'Loading...' : staffError ? 'Error loading staff' : 'No staff found'}
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {staff?.map(s => (
                                        <Option key={s._id} value={s._id}>
                                            {s.firstName} {s.lastName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="course" label={t('course')}>
                                <Select
                                    placeholder={t('selectCourse')}
                                    allowClear
                                    showSearch
                                    loading={coursesLoading}
                                    notFoundContent={coursesLoading ? 'Loading...' : coursesError ? 'Error loading courses' : 'No courses found'}
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {courses?.map(course => (
                                        <Option key={course._id} value={course._id}>
                                            {course.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row> */}

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="clientName" label={t('clientName')}>
                                <Input placeholder={t('enterClientName')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="clientEmail" label={t('clientEmail')}>
                                <Input placeholder={t('enterClientEmail')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="clientPhone" label={t('clientPhone')}>
                                <Input placeholder={t('enterClientPhone')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="currency"
                                label={t('currency')}
                                rules={[{ required: true, message: 'Please select currency' }]}
                                initialValue="USD"
                            >
                                <Select>
                                    <Option value="USD">{t('USD')}</Option>
                                    <Option value="IQD">{t('IQD')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>{t('items')}</Divider>

                    {items.map((item, index) => (
                        <Row key={index} gutter={16} style={{ marginBottom: 8 }}>
                            <Col span={10}>
                                <Input
                                    placeholder={t('description')}
                                    value={item.description}
                                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                />
                            </Col>
                            <Col span={4}>
                                <InputNumber
                                    placeholder={t('quantity')}
                                    min={1}
                                    value={item.quantity}
                                    onChange={(value) => handleItemChange(index, 'quantity', value)}
                                    style={{ width: '100%' }}
                                />
                            </Col>
                            <Col span={4}>
                                <InputNumber
                                    placeholder={t('unitPrice')}
                                    min={0}
                                    precision={2}
                                    value={item.unitPrice}
                                    onChange={(value) => handleItemChange(index, 'unitPrice', value)}
                                    style={{ width: '100%' }}
                                />
                            </Col>
                            <Col span={4}>
                                <Input
                                    value={item.total.toFixed(2)}
                                    disabled
                                />
                            </Col>
                            <Col span={2}>
                                {items.length > 1 && (
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleRemoveItem(index)}
                                    />
                                )}
                            </Col>
                        </Row>
                    ))}

                    <Button
                        type="dashed"
                        onClick={handleAddItem}
                        style={{ width: '100%', marginTop: 8 }}
                        icon={<PlusOutlined />}
                    >
                        {t('addItem')}
                    </Button>

                    <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={8}>
                            <Form.Item name="tax" label={t('tax')} initialValue={0}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="discount" label={t('discount')} initialValue={0}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="paidAmount" label={t('paidAmount')} initialValue={0}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="paymentMethod" label={t('paymentMethod')}>
                        <Select placeholder={t('selectPaymentMethod')}>
                            <Option value="cash">{t('cash')}</Option>
                            <Option value="bank">{t('bank')}</Option>
                            <Option value="card">{t('card')}</Option>
                            <Option value="online">{t('online')}</Option>
                            <Option value="other">{t('other')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="notes" label={t('notes')}>
                        <TextArea rows={3} placeholder={t('enterNotes')} />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                                {editingInvoice ? t('update') : t('create')}
                            </Button>
                            <Button onClick={() => {
                                setModalVisible(false);
                                setEditingInvoice(null);
                                form.resetFields();
                            }}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Payment Modal */}
            <Modal
                title={t('recordPayment')}
                open={paymentModalVisible}
                onCancel={() => {
                    setPaymentModalVisible(false);
                    setSelectedInvoice(null);
                    paymentForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={paymentForm}
                    layout="vertical"
                    onFinish={handlePaymentSubmit}
                >
                    <Form.Item name="amount" label={t('amount')} rules={[{ required: true }]}>
                        <InputNumber
                            min={0}
                            max={selectedInvoice?.remainingAmount}
                            precision={2}
                            style={{ width: '100%' }}
                            prefix="$"
                        />
                    </Form.Item>

                    <Form.Item name="paymentMethod" label={t('paymentMethod')} rules={[{ required: true }]}>
                        <Select>
                            <Option value="cash">{t('cash')}</Option>
                            <Option value="bank">{t('bank')}</Option>
                            <Option value="card">{t('card')}</Option>
                            <Option value="online">{t('online')}</Option>
                            <Option value="other">{t('other')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={paymentMutation.isLoading}>
                                {t('recordPayment')}
                            </Button>
                            <Button onClick={() => {
                                setPaymentModalVisible(false);
                                setSelectedInvoice(null);
                                paymentForm.resetFields();
                            }}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Preview/Print Modal */}
            <Modal
                title={null}
                open={previewModalVisible}
                onCancel={() => {
                    setPreviewModalVisible(false);
                    setSelectedInvoice(null);
                }}
                footer={[
                    <Select
                        key="language"
                        value={printLanguage}
                        onChange={setPrintLanguage}
                        style={{ width: 150, marginRight: 'auto' }}
                    >
                        <Select.Option value="ku">کوردی</Select.Option>
                        <Select.Option value="ar">العربية</Select.Option>
                        <Select.Option value="en">English</Select.Option>
                    </Select>,
                    <Button key="close" onClick={() => {
                        setPreviewModalVisible(false);
                        setSelectedInvoice(null);
                    }}>
                        {t('close')}
                    </Button>,
                    <Button
                        key="print"
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={() => {
                            if (handlePrint) {
                                handlePrint();
                            }
                        }}
                    >
                        {t('print')}
                    </Button>,
                ]}
                width={900}
                destroyOnClose={false}
            >
                <div ref={printRef}>
                    <InvoicePrintTemplate invoice={selectedInvoice} language={printLanguage} />
                </div>
            </Modal>
        </div>
    );
};

export default Invoices;
